import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileUploader } from '@/components/payroll-validator/FileUploader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Info } from 'lucide-react';
import ApiService from '@/services/api';
import {
  FileUploadItem,
  CreateSessionResponse,
  GetUploadUrlResponse,
  RegisterDocumentResponse,
} from '@/types/payroll-validator';

export const PayrollValidatorUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const handleFilesAdded = (newFiles: File[]) => {
    const fileItems: FileUploadItem[] = newFiles.map((file) => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      status: 'pending',
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...fileItems]);
  };

  const handleFileRemove = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const updateFileStatus = (
    fileId: string,
    updates: Partial<FileUploadItem>
  ) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, ...updates } : f))
    );
  };

  const getFileType = (mimeType: string, fileName?: string): string => {
    console.log('getFileType called:', { mimeType, fileName });
    
    // Check MIME type first
    if (mimeType === 'text/csv') return 'csv';
    if (mimeType === 'application/pdf') return 'pdf_payslip';
    if (mimeType === 'text/html') return 'html';
    if (
      mimeType === 'application/vnd.ms-excel' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      return 'excel';
    }
    
    // Fallback to file extension if MIME type is unknown or empty
    if (fileName) {
      const ext = fileName.toLowerCase().split('.').pop();
      console.log('Fallback to extension:', ext);
      if (ext === 'html' || ext === 'htm') {
        console.log('Detected HTML file by extension');
        return 'html';
      }
      if (ext === 'csv') return 'csv';
      if (ext === 'xls' || ext === 'xlsx') return 'excel';
      if (ext === 'pdf') return 'pdf_payslip';
    }
    
    console.log('Falling back to default: csv');
    return 'csv'; // default
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError('Please add at least one file to upload');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Step 1: Create validation session
      const sessionResponse = await ApiService.request<CreateSessionResponse>(
        'createValidationSession',
        {
          method: 'POST',
          skipAuth: true, // Allow anonymous uploads for demo
        }
      );

      const newSessionId = sessionResponse.session_id;
      setSessionId(newSessionId);

      // Step 2: Upload each file
      for (const fileItem of files) {
        try {
          updateFileStatus(fileItem.id, { status: 'uploading', progress: 0 });

          // Get presigned URL
          const urlResponse = await ApiService.request<GetUploadUrlResponse>(
            'getValidationUploadUrl',
            {
              method: 'POST',
              body: {
                sessionId: newSessionId,
                fileName: fileItem.file.name,
                fileType: fileItem.file.type,
              },
              skipAuth: true,
            }
          );

          // Upload to S3
          updateFileStatus(fileItem.id, { progress: 33 });
          const uploadResult = await fetch(urlResponse.uploadUrl, {
            method: 'PUT',
            body: fileItem.file,
            headers: {
              'Content-Type': fileItem.file.type,
            },
            mode: 'cors',
            credentials: 'omit', // Critical: Don't send any credentials to S3
          });

          if (!uploadResult.ok) {
            throw new Error(`Upload failed: ${uploadResult.statusText}`);
          }

          updateFileStatus(fileItem.id, { progress: 66 });

          // Register document with backend
          await ApiService.request<RegisterDocumentResponse>(
            'registerValidationDocument',
            {
              method: 'POST',
              body: {
                sessionId: newSessionId,
                fileName: fileItem.file.name,
                fileType: getFileType(fileItem.file.type, fileItem.file.name),
                fileSize: fileItem.file.size,
                s3Key: urlResponse.fileKey,
                mimeType: fileItem.file.type,
              },
              skipAuth: true,
            }
          );

          updateFileStatus(fileItem.id, {
            status: 'completed',
            progress: 100,
          });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Upload failed';
          updateFileStatus(fileItem.id, {
            status: 'failed',
            error: errorMessage,
          });
          throw err;
        }
      }

      // Step 3: Trigger validation
      await ApiService.request(
        'startValidation',
        {
          method: 'POST',
          urlParams: { sessionId: newSessionId },
          skipAuth: true,
        }
      );

      // Redirect to results page
      navigate(`/payroll-validator/session/${newSessionId}`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred during upload';
      setError(errorMessage);
      setUploading(false);
    }
  };

  const allFilesCompleted = files.length > 0 && files.every((f) => f.status === 'completed');
  const hasFailedFiles = files.some((f) => f.status === 'failed');

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Payroll Document Validator
        </h1>
        <p className="text-lg text-gray-600">
          Upload your payroll exports and payslips to instantly check for issues
          that could cost you thousands
        </p>
      </div>

      {/* Info Alert */}
      <Alert className="mb-6 bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-900">What to upload</AlertTitle>
        <AlertDescription className="text-blue-800">
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Employee data CSV/Excel export from your current payroll provider</li>
            <li>Recent payslip PDFs for cross-validation</li>
            <li>Pay history CSV if available</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* File Uploader */}
      <FileUploader
        files={files}
        onFilesAdded={handleFilesAdded}
        onFileRemove={handleFileRemove}
      />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Success Message */}
      {allFilesCompleted && !uploading && (
        <Alert className="mt-4 bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-900">Files Uploaded Successfully</AlertTitle>
          <AlertDescription className="text-green-800">
            All files have been uploaded. Click "Validate Documents" to start the validation process.
          </AlertDescription>
        </Alert>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex gap-4">
        <Button
          onClick={handleUpload}
          disabled={files.length === 0 || uploading || hasFailedFiles}
          size="lg"
          className="flex-1"
        >
          {uploading ? (
            <>
              <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Uploading...
            </>
          ) : (
            'Validate Documents'
          )}
        </Button>

        {files.length > 0 && !uploading && (
          <Button
            variant="outline"
            onClick={() => setFiles([])}
            size="lg"
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Features Section */}
      <Card className="mt-12">
        <CardHeader>
          <CardTitle>What We Check</CardTitle>
          <CardDescription>
            Our validator runs 50+ checks across critical payroll areas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Pension Compliance</h3>
                <p className="text-sm text-gray-600">
                  Auto-enrolment minimums, scheme types, contribution rates
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-green-600 font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Tax Codes</h3>
                <p className="text-sm text-gray-600">
                  Invalid codes, emergency tax, incorrect basis
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-purple-600 font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">NI Categories</h3>
                <p className="text-sm text-gray-600">
                  Age mismatches, incorrect rates, pension age issues
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <span className="text-orange-600 font-semibold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">HMRC RTI Data</h3>
                <p className="text-sm text-gray-600">
                  Missing Pay IDs, duplicate records, submission issues
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <span className="text-red-600 font-semibold text-sm">5</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Pay History</h3>
                <p className="text-sm text-gray-600">
                  YTD discrepancies, missing periods, calculation errors
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                <span className="text-yellow-600 font-semibold text-sm">6</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Cross-Document Validation</h3>
                <p className="text-sm text-gray-600">
                  Payslip vs CSV mismatches, data consistency
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

